import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardContext, type DashboardPeriod } from '../DashboardContext';
import { fetchAttendanceOverview } from '../../../api/dashboard/attendance';
import { fetchPayrollOverview } from '../../../api/dashboard/payroll';
import { fetchEmployeesOverview } from '../../../api/dashboard/employees';
import { fetchProjectsOverview } from '../../../api/dashboard/projects';
import { fetchUpcomingHolidays } from '../../../api/dashboard/holidays';
import { useEnabledModules } from './useEnabledModules';
import {
  mapAttendanceToKPIs,
  mapPayrollToKPIs,
  mapEmployeesToKPIs,
  mapProjectsToKPIs,
  mapHolidaysToCards,
  type KPIMetric,
  type HolidayCardData,
} from '../mappers';

// =============================================================================
// Types
// =============================================================================

export type UseDashboardDataResult = {
  loading: boolean;
  error: Error | null;
  primaryKPIs: KPIMetric[];
  secondaryKPIs: {
    attendance: KPIMetric[];
    payroll: KPIMetric[];
    employees: KPIMetric[];
    projects: KPIMetric[];
  };
  holidays: HolidayCardData[];
  refetch: () => void;
};

// =============================================================================
// Module gating
// =============================================================================

/**
 * The company module each KPI group belongs to (ids from `navigationModules.ts`).
 *
 * A company that does not have Payroll must not be shown a "Payroll Cost" of 0 — an unpurchased
 * module has no number, and rendering it as zero states a fact about the business that is not true.
 * The sidebar has always asked this question; the dashboard never did, which is why an
 * engagement-only tenant landed on four cards reading 0%, 0, 0 and 0.
 *
 * Disabled groups are not fetched at all, so their endpoints are not called either.
 */
const DASHBOARD_MODULE_IDS = {
  attendance: 'attendance',
  payroll: 'payroll',
  employees: 'people',
  projects: 'projects',
} as const;

// =============================================================================
// Helper: Period to Date Range
// =============================================================================

/**
 * Convert a DashboardPeriod to { from, to } date strings (YYYY-MM-DD).
 */
function periodToDateRange(period: DashboardPeriod): { from: string; to: string } {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-indexed

  const formatDate = (d: Date): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  switch (period) {
    case 'this-month': {
      const from = new Date(year, month, 1);
      return { from: formatDate(from), to: formatDate(today) };
    }
    case 'last-month': {
      const from = new Date(year, month - 1, 1);
      const to = new Date(year, month, 0); // Last day of previous month
      return { from: formatDate(from), to: formatDate(to) };
    }
    case 'last-3m': {
      const from = new Date(year, month - 3, today.getDate());
      return { from: formatDate(from), to: formatDate(today) };
    }
    case 'ytd': {
      const from = new Date(year, 0, 1);
      return { from: formatDate(from), to: formatDate(today) };
    }
    default: {
      // Default to this month
      const from = new Date(year, month, 1);
      return { from: formatDate(from), to: formatDate(today) };
    }
  }
}

// =============================================================================
// Hook: useDashboardData
// =============================================================================

/**
 * Custom hook that fetches all dashboard data and returns mapped props
 * ready for Tailwind components.
 *
 * Uses DashboardContext for period, scope, and currency settings.
 * Fetches attendance, payroll, employees, and projects in parallel.
 * Maps API responses through mapper functions.
 */
export function useDashboardData(): UseDashboardDataResult {
  const { period, scope, currencyLabel } = useDashboardContext();
  const { t } = useTranslation();
  const { resolved: modulesResolved, isModuleEnabled } = useEnabledModules();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [primaryKPIs, setPrimaryKPIs] = useState<KPIMetric[]>([]);
  const [secondaryKPIs, setSecondaryKPIs] = useState<{
    attendance: KPIMetric[];
    payroll: KPIMetric[];
    employees: KPIMetric[];
    projects: KPIMetric[];
  }>({
    attendance: [],
    payroll: [],
    employees: [],
    projects: [],
  });
  const [holidays, setHolidays] = useState<HolidayCardData[]>([]);

  // Ref to track if a fetch is in progress (for avoiding race conditions)
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(async (signal: AbortSignal) => {
    // Increment fetch ID to track this specific fetch
    const currentFetchId = ++fetchIdRef.current;

    setLoading(true);
    setError(null);

    const { from, to } = periodToDateRange(period);
    const currency = currencyLabel ?? '$';

    try {
      // Only fetch the groups this company actually has. A disabled module contributes no request
      // and no card — not a card reading zero.
      const wantAttendance = isModuleEnabled(DASHBOARD_MODULE_IDS.attendance);
      const wantPayroll = isModuleEnabled(DASHBOARD_MODULE_IDS.payroll);
      const wantEmployees = isModuleEnabled(DASHBOARD_MODULE_IDS.employees);
      const wantProjects = isModuleEnabled(DASHBOARD_MODULE_IDS.projects);

      // Fetch all enabled data in parallel (including approvals and holidays)
      const [attendanceData, payrollData, employeesData, projectsData, holidaysData] = await Promise.all([
        wantAttendance ? fetchAttendanceOverview({ from, to, scope }, signal) : Promise.resolve(null),
        wantPayroll ? fetchPayrollOverview({ from, to, scope }, signal) : Promise.resolve(null),
        wantEmployees ? fetchEmployeesOverview({ from, to }, signal) : Promise.resolve(null),
        wantProjects ? fetchProjectsOverview({ from, to }, signal) : Promise.resolve(null),
        fetchUpcomingHolidays({ days: 30 }, signal).catch(() => []), // Graceful fallback
      ]);

      // Check if this fetch is still the current one (avoid stale updates)
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      // Map holidays
      const mappedHolidays = mapHolidaysToCards(holidaysData ?? []);

      // Map each fetched response through its mapper, skipping the groups this company lacks.
      const primaries: KPIMetric[] = [];
      const secondaries: UseDashboardDataResult['secondaryKPIs'] = {
        attendance: [],
        payroll: [],
        employees: [],
        projects: [],
      };

      if (attendanceData) {
        const attendance = mapAttendanceToKPIs(attendanceData, t);
        primaries.push(attendance.primary);
        secondaries.attendance = attendance.secondary;
      }
      if (payrollData) {
        const payroll = mapPayrollToKPIs(payrollData, currency, t);
        primaries.push(payroll.primary);
        secondaries.payroll = payroll.secondary;
      }
      if (employeesData) {
        const employees = mapEmployeesToKPIs(employeesData, t);
        primaries.push(employees.primary);
        secondaries.employees = employees.secondary;
      }
      if (projectsData) {
        const projects = mapProjectsToKPIs(projectsData, t);
        primaries.push(projects.primary);
        secondaries.projects = projects.secondary;
      }

      setPrimaryKPIs(primaries);
      setSecondaryKPIs(secondaries);
      setHolidays(mappedHolidays);
      setError(null);
    } catch (err) {
      // Check if this fetch is still current
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      // Don't set error for abort (user navigation/cleanup)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      setError(err instanceof Error ? err : new Error('Failed to fetch dashboard data'));
    } finally {
      // Only update loading if this is still the current fetch
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [period, scope, currencyLabel, t, isModuleEnabled]);

  // AbortController ref for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    // Abort any in-progress fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new controller and fetch
    abortControllerRef.current = new AbortController();
    fetchData(abortControllerRef.current.signal);
  }, [fetchData]);

  // Fetch on mount and when dependencies change. Held until the module config has resolved, so a
  // card for a module the company does not have is never painted and then pulled back.
  useEffect(() => {
    if (!modulesResolved) return;

    // Abort any previous fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new controller
    abortControllerRef.current = new AbortController();
    fetchData(abortControllerRef.current.signal);

    // Cleanup: abort on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, modulesResolved]);

  return {
    loading,
    error,
    primaryKPIs,
    secondaryKPIs,
    holidays,
    refetch,
  };
}
